import { Check, Search, X } from 'lucide-react-native';
import { useCallback } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import { HStack, ProgressBar, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { Sheet } from '../../../components/primitives/Sheet';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import type { WebSearchStatusEvent } from '../types';

interface WebSearchProgressSheetProps {
  visible: boolean;
  onClose: () => void;
  onCancel: () => void;
  lastEvent: WebSearchStatusEvent | null;
  leadName?: string;
}

const STATUS_ORDER = ['searching', 'scraping', 'extracting'] as const;

export function WebSearchProgressSheet({
  visible,
  onClose,
  onCancel,
  lastEvent,
  leadName,
}: WebSearchProgressSheetProps) {
  const { t } = useTranslation();
  const status = lastEvent?.status;

  const isTerminal =
    status === 'completed' ||
    status === 'failed' ||
    status === 'cancelled' ||
    status === 'no_results' ||
    status === 'awaiting_approval';

  const progress =
    lastEvent?.linksExplored && lastEvent?.totalLinks
      ? Math.round((lastEvent.linksExplored / lastEvent.totalLinks) * 100)
      : 0;

  const currentStepIndex = status
    ? STATUS_ORDER.indexOf(status as (typeof STATUS_ORDER)[number])
    : -1;

  const handleSheetClose = useCallback(() => {
    if (isTerminal) onClose();
  }, [isTerminal, onClose]);

  return (
    <Sheet visible={visible} onClose={handleSheetClose} position="center" size="sm" hideClose>
      <View>
        {isTerminal && (
          <Pressable
            onPress={onClose}
            accessibilityLabel={t('common.actions.close')}
            style={styles.closeBtn}
          >
            <X size={18} color={colors.text.tertiary} strokeWidth={2} />
          </Pressable>
        )}

        <VStack spacing="lg" style={{ alignItems: 'center' }}>
          {!isTerminal && (
            <>
              <ActivityIndicator size="large" color={colors.primary[500]} />
              <Text variant="h6" color={colors.text.primary} weight="semibold">
                {t('leads.webSearch.title')}
              </Text>
              {leadName && (
                <Text variant="body" color={colors.text.secondary}>
                  {leadName}
                </Text>
              )}
            </>
          )}

          <VStack spacing="sm" style={{ width: '100%' }}>
            {STATUS_ORDER.map((step, idx) => {
              const isActive = step === status;
              const isComplete = currentStepIndex > idx;
              const _isPending = currentStepIndex < idx && currentStepIndex >= 0;

              return (
                <HStack key={step} spacing="sm" style={{ alignItems: 'center' }}>
                  {isComplete ? (
                    <Check size={16} color={colors.status.success.main} />
                  ) : isActive ? (
                    <Search size={16} color={colors.primary[400]} />
                  ) : (
                    <View style={styles.pendingDot} />
                  )}
                  <Text
                    variant="bodySmall"
                    color={
                      isActive
                        ? colors.text.primary
                        : isComplete
                          ? colors.status.success.main
                          : colors.text.disabled
                    }
                    weight={isActive ? 'semibold' : 'normal'}
                  >
                    {t(`leads.webSearch.${step}`)}
                  </Text>
                </HStack>
              );
            })}
          </VStack>

          {progress > 0 && !isTerminal && (
            <VStack spacing="xs" style={{ width: '100%' }}>
              <ProgressBar value={progress} animated />
              <Text variant="caption" color={colors.text.tertiary} align="center">
                {t('leads.webSearch.reviewingLinks')} ({lastEvent?.linksExplored}/
                {lastEvent?.totalLinks})
              </Text>
            </VStack>
          )}

          {lastEvent?.currentUrl && !isTerminal && (
            <Text
              variant="caption"
              color={colors.text.disabled}
              numberOfLines={1}
              style={{ width: '100%' }}
            >
              {t('leads.webSearch.currentlyVisiting')}: {lastEvent.currentUrl}
            </Text>
          )}

          {status === 'failed' && (
            <Text variant="body" color={colors.status.error.main} align="center">
              {lastEvent?.error || t('leads.webSearch.errorToast')}
            </Text>
          )}

          {status === 'cancelled' && (
            <Text variant="body" color={colors.text.secondary} align="center">
              {t('leads.webSearch.cancelledDescription')}
            </Text>
          )}

          {status === 'no_results' && (
            <Text variant="body" color={colors.text.secondary} align="center">
              {t('leads.webSearch.noResultsDescription')}
            </Text>
          )}

          {!isTerminal && (
            <Button variant="ghost" size="sm" onPress={onCancel}>
              {t('leads.webSearch.cancel')}
            </Button>
          )}
        </VStack>
      </View>
    </Sheet>
  );
}

const styles = StyleSheet.create({
  closeBtn: {
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 1,
    padding: 4,
  },
  pendingDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: colors.border.subtle,
  },
});
